import { EventBus } from "./event_bus.js";
import { Effects } from "./effects.js";
import { MessageBus } from "./message_bus.js";
import { Retry } from "./retry.js";
import { APP } from "./app.js";
import { Utils } from "./utils.js";

export class Page {
  steps
  developerRequirementsChecks
  termsAndConditionsCheck

  titlePortionElement
  playtestRoundInfoBlock

  coverImgElement
  titleElement
  descriptionElement
  playtestingRewardBlock
  playtesterExpectationsBlock
  playtestingSessionDurationBlock
  fullGameplayDurationBlock
  genresBlock
  developerContactBlock
  acceptPlaySessionButton
  unsupportedBrowserNotice
  developerRequirementsBlock
  extraInformationBlock
  afterSessionInfo
  afterThanksBlock
  afterThanksLink
  afterThanksRecordingsNote

  stepsSectionBlock
  stepperElement
  pageBlock


  constructor({
    playtestRound
  } = {}) {
    this.playtestRound = playtestRound

    // PlaytestRound basic description
    this.titlePortionElement = document.querySelector("#title-portion");
    this.playtestRoundInfoBlock = document.querySelector("#playtest-round-info-block");
    this.coverImgElement = document.querySelector("#playtest-round-cover-img");
    this.titleElement = document.querySelector("#playtest-round-title");
    this.descriptionElement = document.querySelector("#playtest-round-description");
    this.playtestingRewardBlock = document.querySelector("#playtest-round-playtesting-reward");
    this.playtesterExpectationsBlock = document.querySelector("#playtest-round-playtester-expectations");
    this.playtestingSessionDurationBlock = document.querySelector("#playtest-round-playtesting-session-duration");
    this.fullGameplayDurationBlock = document.querySelector("#playtest-round-full-gameplay-duration");
    this.genresBlock = document.querySelector("#playtest-round-genres");
    this.developerContactBlock = document.querySelector("#playtest-round-developer-contact");
    this.acceptPlaySessionButton = document.querySelector("#playtest-round-accept-play-session-button");
    this.unsupportedBrowserNotice = document.querySelector("#unsupported-browser-notice");
    this.developerRequirementsBlock = document.querySelector("#step-developer-requirements");
    this.extraInformationBlock = document.querySelector("#step-extra-information")
    this.afterSessionInfoBlock = document.querySelector("#step-after-session-info")
    this.afterThanksBlock = document.querySelector("#after-thanks");
    this.afterThanksLink = document.querySelector("#after-thanks-link");
    this.afterThanksRecordingsNote = document.querySelector("#after-thanks-recordings-note");
    this.pageBlock = document.querySelector("#page");

    // Steps section
    this.steps = [];
    this.stepsSectionBlock = document.querySelector("#steps-section");
    this.stepperElement = document.querySelector("#steps-stepper");
    const stepChanged = (step) => { this.stepChanged(step); };
    this.stepDeveloperRequirements = new Page.Step(this.developerRequirementsBlock, this.steps, stepChanged);
    this.stepExtraInformation = new Page.Step(this.extraInformationBlock, this.steps, stepChanged);
    this.stepDownloadGame = new Page.Step(this.stepsSectionBlock.querySelector("#step-download-game"), this.steps, stepChanged);
    this.stepTermsAndConditions = new Page.Step(this.stepsSectionBlock.querySelector("#step-terms-and-conditions"), this.steps, stepChanged);
    this.stepRecordingRoom = new Page.Step(this.stepsSectionBlock.querySelector("#step-recording-room"), this.steps, stepChanged);
    this.stepFinalThoughts = new Page.Step(this.stepsSectionBlock.querySelector("#step-final-thoughts"), this.steps, stepChanged);
    this.stepAfterSessionInfo = new Page.Step(this.afterSessionInfoBlock, this.steps, stepChanged);

    this.termsAndConditionsCheck = this.stepTermsAndConditions.block.querySelector("input[type=checkbox]");
  }

  checkIfPlaytestRoundIsStillOpen() {
    if (this.playtestRound.isFinished) {
      Effects.delayedBlurIn(this.pageBlock);
      MessageBus.info(
        "This PlaySession is already closed. Thank you so much for your interest in helping us testing this game ;)",
        () => {
          this.afterThanks();
        }
      )
      return false;
    } else {
      return true;
    }
  }

  init() {
    this.setPageTitle();
    this.setEvents();
    this.setListeners();
    this.fillPlaytestRoundInfo();
    this.buildStepper();
    this.showPlaytestRoundInfo();
    window.scrollTo({ top: 0 });
  }

  setListeners() {
    EventBus.addListener(
      "recording-session-finished",
      () => { this.recordingSessionFinished(); }
    );

    EventBus.addListener(
      "final-thoughts-finished",
      () => { this.finalThoughtsFinished(); }
    );

    EventBus.addListener(
      "all-steps-finished",
      () => { this.allStepsFinished(); }
    );
  }

  setEvents() {
    // I'm in button
    this.acceptPlaySessionButton.addEventListener("click", () => {
      Effects.delayedFadeOut(this.acceptPlaySessionButton);
      this.showStepsSectionBlock();
      this.steps[0].show();
    });

    // Extra information next button
    this.stepExtraInformation.nextButton.addEventListener("click", () => {
      this.stepExtraInformation.advance();
    });

    // Download game next button
    this.stepDownloadGame.nextButton.addEventListener("click", () => {
      this.stepDownloadGame.advance();
    });

    // Terms and conditions
    this.termsAndConditionsCheck.addEventListener("click", () => {
      MessageBus.debug("Terms and Conditions accepted");
      APP.playcocolaAPI.sendSystemInfo();
      // Remembered name and email go out only after consent
      APP.finalThoughts.sendInfoIfAlreadyRemembered();
      this.termsAndConditionsCheck.setAttribute("disabled", true);
      this.stepTermsAndConditions.advance();
    });

    // After Session Done button: advancing from the last step fires all-steps-finished
    this.stepAfterSessionInfo.nextButton.addEventListener("click", () => {
      this.stepAfterSessionInfo.advance();
    });

    // Window closes
    window.addEventListener("beforeunload", (event) => {
      this.windowIsClosing(event);
    }, { capture: true });
  }

  // Text, never markup: a link can point this page at any server, and this origin is trusted with the sign-in cookie
  setPageTitle() {
    document.title = "Playcocola is testing " + this.playtestRound.title;
    this.titlePortionElement.textContent = this.playtestRound.title;
  }

  showStepsSectionBlock() {
    Effects.delayedFadeIn(this.stepsSectionBlock);
  }

  // Built once the steps without content are removed, so the stepper only lists the real ones
  buildStepper() {
    this.stepperElement.replaceChildren(...this.steps.map((step, index) => step.buildStepperItem(index)));
  }

  // A new current step: bring the stepper and its panel into view
  stepChanged(step) {
    if (!step.isCurrent) {
      return;
    }

    const top = this.stepsSectionBlock.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight / 3) {
      this.stepsSectionBlock.scrollIntoView({ block: "start", behavior: Effects.scrollBehavior() });
    }
  }

  developerRequirementsCheckChecked() {
    this.stepDeveloperRequirements.block.querySelectorAll("input[type=checkbox]:checked").forEach((element) => {
      element.setAttribute("disabled", true);
    });

    if (this.stepDeveloperRequirements.block.querySelectorAll("input[type=checkbox]:not(:checked)").length === 0) {
      this.stepDeveloperRequirements.advance();
    }
  }

  fillPlaytestRoundInfo() {
    // Basic
    if (this.playtestRound.coverUrl) {
      this.coverImgElement.src = this.playtestRound.coverUrl;
    } else {
      this.coverImgElement.remove();
      this.coverImgElement = null;
    }

    this.titleElement.textContent = this.playtestRound.title;
    this.descriptionElement.innerHTML = Utils.markdown(this.playtestRound.description);

    if (this.playtestRound.playtestingSessionMinutes) {
      this.playtestingSessionDurationBlock.querySelector(".mins").textContent = this.playtestRound.playtestingSessionMinutes;
    } else {
      this.playtestingSessionDurationBlock.remove();
      this.playtestingSessionDurationBlock = null;
    }

    if (this.playtestRound.fullGameplayMinutes) {
      this.fullGameplayDurationBlock.querySelector(".mins").textContent = this.playtestRound.fullGameplayMinutes;
    } else {
      this.fullGameplayDurationBlock.remove();
      this.fullGameplayDurationBlock = null;
    }

    if (this.playtestRound.playtesterReward) {
      this.playtestingRewardBlock.querySelector(".card-text").innerHTML = Utils.markdown(this.playtestRound.playtesterReward || "");
    } else {
      this.playtestingRewardBlock.remove();
      this.playtestingRewardBlock = null;
    }

    if (this.playtestRound.playtesterExpectations) {
      this.playtesterExpectationsBlock.querySelector(".card-text").innerHTML = Utils.markdown(this.playtestRound.playtesterExpectations || "");
    } else {
      this.playtesterExpectationsBlock.remove();
      this.playtesterExpectationsBlock = null;
    }

    if (this.playtestRound.genres.length !== 0) {
      this.fillGenres(this.playtestRound.genres)
    } else {
      this.genresBlock.remove();
      this.genresBlock = null;
    }

    this.fillDeveloperContact();

    // Steps
    if (this.playtestRound.requiredCheckboxes.length !== 0) {
      this.fillDeveloperRequirements(this.playtestRound.requiredCheckboxes);
    } else {
      this.stepDeveloperRequirements.remove();
      this.stepDeveloperRequirements = null;
    }

    if (this.playtestRound.gameBuildUrl) {
      this.stepDownloadGame.stepContentElement.querySelector("a").href = this.playtestRound.gameBuildUrl;
    } else {
      this.stepDownloadGame.remove();
      this.stepDownloadGame = null;
    }

    if (this.playtestRound.afterRequiredCheckboxesDescription) {
      this.stepExtraInformation.setBody(this.playtestRound.afterRequiredCheckboxesDescription);
    } else {
      this.stepExtraInformation.remove();
      this.stepExtraInformation = null;
    }

    if (this.playtestRound.afterSessionFinishedDescription) {
      this.stepAfterSessionInfo.setBody(this.playtestRound.afterSessionFinishedDescription);
    } else {
      this.stepAfterSessionInfo.remove();
      this.stepAfterSessionInfo = null;
    }
  }

  fillGenres(genres) {
    const templateBadge = this.genresBlock.querySelector("span");

    this.genresBlock.innerHTML = "";

    genres.forEach((genre) => {
      const newBadge = templateBadge.cloneNode(true);
      newBadge.textContent = genre;
      this.genresBlock.appendChild(newBadge);

      const space = document.createTextNode("\u00A0");
      this.genresBlock.appendChild(space);
    });
  }

  // The developer's channels on a Bounty recording, Playcocola's address on a free one (backend
  // docs/bounty_program.md, Developer contact). Text, values and https links only: never markup from
  // the server. A server from before the feature sends no support_email: no panel.
  fillDeveloperContact() {
    const supportEmail = this.playtestRound.supportEmail;

    if (!supportEmail) {
      this.developerContactBlock.remove();
      this.developerContactBlock = null;
      return;
    }

    const block = this.developerContactBlock;
    block.querySelectorAll('[data-contact="support"]').forEach((link) => { link.href = `mailto:${supportEmail}`; });

    const channels = Array.isArray(this.playtestRound.developerContact?.channels) ? this.playtestRound.developerContact.channels : [];
    const contentOf = (kind) => channels.find((channel) => channel?.kind === kind && typeof channel.content === "string" && channel.content)?.content;
    const email = contentOf("email");
    const discord = contentOf("discord");
    const other = channels.find((channel) => channel?.kind === "other" && typeof channel.label === "string" && channel.label && Page.isHttpsUrl(channel.content));

    this.#fillContactItem("email", email, (item) => {
      const link = item.querySelector("a");
      link.href = `mailto:${email}`;
      link.textContent = email;
    });
    this.#fillContactItem("discord-invite", Page.isHttpsUrl(discord), (item) => {
      item.querySelector("a").href = discord;
    });
    this.#fillContactItem("discord-username", discord && !discord.includes("/"), (item) => {
      const input = item.querySelector("input");
      const button = item.querySelector("button");
      input.value = discord;
      button.addEventListener("click", () => { this.copyDeveloperContact(input, button.querySelector(".bi")); });
    });
    this.#fillContactItem("other", other, (item) => {
      const link = item.querySelector("a");
      link.href = other.content;
      link.textContent = other.label;
    });

    if (block.querySelector("[data-contact-kind]")) {
      const name = this.playtestRound.developerContact.developer_name;
      block.querySelector('[data-contact="name"]').textContent = typeof name === "string" && name ? name : "the developer";
      block.querySelector('[data-contact="fallback"]').remove();
    } else {
      block.querySelector('[data-contact="developer"]').remove();
      block.querySelector(".pc-panel-title").textContent = "Questions?";
    }
  }

  // An item of the contact list filled, or removed when there is nothing to show
  #fillContactItem(kind, shown, fill) {
    const item = this.developerContactBlock.querySelector(`[data-contact-kind="${kind}"]`);

    if (shown) {
      fill(item);
    } else {
      item.remove();
    }
  }

  async copyDeveloperContact(input, icon) {
    try {
      await navigator.clipboard.writeText(input.value);
      icon.classList.replace("bi-clipboard", "bi-clipboard-check");
      setTimeout(() => { icon.classList.replace("bi-clipboard-check", "bi-clipboard"); }, 1_500);
    } catch {
      input.select(); // No clipboard access: selected, for Ctrl+C
    }
  }

  static isHttpsUrl(value) {
    if (typeof value !== "string") return false;

    try {
      return new URL(value).protocol === "https:";
    } catch {
      return false;
    }
  }

  fillDeveloperRequirements(requirements) {
    const templateCheck = this.stepDeveloperRequirements.stepContentElement.querySelector(".form-check");

    this.stepDeveloperRequirements.stepContentElement.innerHTML = "";

    requirements.forEach((requirement) => {
      const id = crypto.randomUUID();
      const newCheck = templateCheck.cloneNode(true);
      newCheck.querySelector("input").id = id;
      newCheck.querySelector("label").htmlFor = id;
      newCheck.querySelector("label").innerHTML = Utils.markdown(requirement);

      newCheck.querySelector("input").addEventListener("change", () => {
        this.developerRequirementsCheckChecked();
      });

      this.stepDeveloperRequirements.stepContentElement.appendChild(newCheck);
    });
  }

  // Show Elements :: INI
  // One quick entrance for the whole card, then the button
  showPlaytestRoundInfo() {
    Effects.delayedFadeIn(this.playtestRoundInfoBlock);

    if (APP.recordingManager.isBrowserSupported) {
      Effects.delayedFadeIn(this.acceptPlaySessionButton, 150);
    } else {
      this.showUnsupportedBrowserNotice();
    }

    this.makeLinksTargetBlank(this.descriptionElement);
    this.makeLinksTargetBlank(this.playtestingRewardBlock);
    this.makeLinksTargetBlank(this.developerRequirementsBlock);
    this.makeLinksTargetBlank(this.extraInformationBlock);
    this.makeLinksTargetBlank(this.afterSessionInfoBlock);
  }
  // Show Elements :: END

  // Told before starting the steps, instead of after picking a screen (C9)
  showUnsupportedBrowserNotice() {
    this.acceptPlaySessionButton.classList.add("d-none");
    Effects.delayedFadeIn(this.unsupportedBrowserNotice, 150);
    MessageBus.debug("Unsupported browser, missing: " + APP.recordingManager.missingCapabilities.join(", "));
  }

  makeLinksTargetBlank(element) {
    if (!element) {
      return;
    }

    element.querySelectorAll("a").forEach((link) => { link.setAttribute('target', '_blank'); });
  }

  recordingSessionFinished() {
    this.stepRecordingRoom.advance();
  }

  finalThoughtsFinished() {
    this.stepFinalThoughts.advance();
  }

  // The thanks wait until the server confirmed the end of the session (B11)
  async allStepsFinished() {
    Effects.delayedBlurIn(this.pageBlock, 300);
    Effects.delayedFadeOut(this.stepsSectionBlock, 2000);

    const [isFinalized] = await Promise.all([
      APP.recordingManager.sessionFinalized(),
      Retry.wait(2000)
    ]);

    if (isFinalized) {
      MessageBus.thanks(() => {
        this.afterThanks();
      });
    } else {
      MessageBus.info(
        "Your video is uploaded, but we could not confirm the end of the Play Session. Please [contact support](mailto:support@playcocola.com) so we can check it.",
        () => { this.afterThanks(); }
      );
    }
  }

  afterThanks() {
    this.pointThanksToRecordings();
    Effects.delayedFadeOut(this.stepsSectionBlock);
    Effects.delayedFadeOut(this.playtestRoundInfoBlock);
    Effects.delayedFadeIn(this.afterThanksBlock);
    Effects.delayedBlurOut(this.pageBlock);
    this.scrollToTop();
  }

  // A recognised playtester goes to their own recordings instead of the ongoing Play Sessions
  pointThanksToRecordings() {
    const recordingsUrl = APP.playtesterIdentity.recordingsUrl;

    if (recordingsUrl) {
      this.afterThanksLink.href = recordingsUrl;
      this.afterThanksLink.textContent = "See your Play Sessions";
      this.afterThanksRecordingsNote.classList.remove("d-none");
    }
  }

  windowIsClosing(event) {
    console.log("WindowIsClosing()");

    if (APP.playtesterIdentity.isUnlinking) {
      event.preventDefault();
      return event.returnValue = "Your Play Session is still leaving your account. Are you sure you want to close the window?";
    }

    if (APP.uploader.isUploadingActive()) {
      event.preventDefault();
      return event.returnValue = "The video is currently uploading. Are you sure you want to close the window?";
    }

    if (APP.recordingManager.isRecording) {
      event.preventDefault();
      return event.returnValue = "The recording is currently active. Are you sure you want to close the window?";
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: Effects.scrollBehavior() });
  }

  // One step of the flow: a panel (#step-*) and its item in the stepper. Only the
  // current step's panel is shown; a done step can be opened again from the
  // stepper to read it, above the current one, when it is marked data-step-reviewable.
  static Step = class Step {
    block
    stepContentElement
    nextButton
    label
    isReviewable
    state
    isReviewing
    isRemoved
    allSteps
    stepperItem
    stepperButton
    #onChange

    constructor(block, allSteps, onChange = null) {
      this.block = block;
      this.stepContentElement = this.block.querySelector(".step-content");
      this.nextButton = this.block.querySelector(".next-button");
      this.label = this.block.dataset.stepLabel;
      this.isReviewable = "stepReviewable" in this.block.dataset;
      this.block.querySelector(".pc-step-close")?.addEventListener("click", () => { this.closeReview(); });

      this.state = "todo";
      this.isReviewing = false;
      this.isRemoved = false;
      this.stepperItem = null;
      this.#onChange = onChange;

      this.allSteps = allSteps;
      this.allSteps.push(this);
    }

    get isCurrent() {
      return this.state === "current";
    }

    setBody(markdownText) {
      this.stepContentElement.innerHTML = Utils.markdown(markdownText);
    }

    show() {
      this.allSteps.forEach((step) => step.closeReview());
      Effects.delayedFadeIn(this.block);
      this.#setState("current");
    }

    hide() {
      this.isReviewing = false;
      this.block.classList.remove("is-review");
      Effects.delayedFadeOut(this.block);
      this.#setState("done");
    }

    // The only way forward. A step that is not current is ignored, so a double
    // click or a Next in a reviewed panel can't skip or repeat a step.
    advance() {
      if (!this.isCurrent) {
        return;
      }

      this.hide();
      this.next()?.show();
    }

    toggleReview() {
      if (this.isReviewing) {
        this.closeReview();
      } else {
        this.openReview();
      }
    }

    openReview() {
      if (this.state !== "done" || !this.isReviewable) {
        return;
      }

      this.allSteps.forEach((step) => { if (step !== this) step.closeReview(); });
      this.isReviewing = true;
      this.block.classList.add("is-review");
      Effects.delayedFadeIn(this.block);
      this.#paint();
      this.block.scrollIntoView({ block: "nearest", behavior: Effects.scrollBehavior() });
    }

    closeReview() {
      if (!this.isReviewing) {
        return;
      }

      this.isReviewing = false;
      this.block.classList.remove("is-review");
      Effects.delayedFadeOut(this.block);
      this.#paint();
    }

    remove() {
      this.block.remove();
      this.stepperItem?.remove();

      const index = this.allSteps.indexOf(this);
      this.allSteps.splice(index, index !== -1 ? 1 : 0);

      this.isRemoved = true;
    }

    next() {
      const index = this.allSteps.indexOf(this);
      if ((index + 1) === this.allSteps.length) {
        EventBus.event("all-steps-finished");
        return null;
      } else {
        return this.allSteps[index + 1];
      }
    }

    // <li class="pc-step"><button class="pc-step-button"><span class="pc-step-icon">…</span><span class="pc-step-label">…</span></button></li>
    buildStepperItem(index) {
      this.stepperItem = document.createElement("li");
      this.stepperItem.className = "pc-step";

      this.stepperButton = document.createElement("button");
      this.stepperButton.type = "button";
      this.stepperButton.className = "pc-step-button";
      this.stepperButton.setAttribute("aria-controls", this.block.id);
      this.stepperButton.addEventListener("click", () => { this.toggleReview(); });

      const icon = document.createElement("span");
      icon.className = "pc-step-icon";
      icon.setAttribute("aria-hidden", "true");
      const number = document.createElement("span");
      number.className = "pc-step-number";
      number.textContent = index + 1;
      const check = document.createElement("i");
      check.className = "bi bi-check-lg";
      icon.append(number, check);

      const label = document.createElement("span");
      label.className = "pc-step-label";
      label.textContent = this.label;

      this.stepperButton.append(icon, label);
      this.stepperItem.append(this.stepperButton);
      this.#paint();

      return this.stepperItem;
    }

    #setState(state) {
      this.state = state;
      this.block.dataset.stepState = state;
      this.#paint();
      this.#onChange?.(this);
    }

    #paint() {
      if (!this.stepperItem) {
        return;
      }

      const isDone = this.state === "done";
      this.stepperItem.classList.toggle("is-done", isDone);
      this.stepperItem.classList.toggle("is-current", this.isCurrent);
      this.stepperButton.disabled = !(isDone && this.isReviewable);

      if (this.isCurrent) {
        this.stepperButton.setAttribute("aria-current", "step");
      } else {
        this.stepperButton.removeAttribute("aria-current");
      }

      if (isDone && this.isReviewable) {
        this.stepperButton.setAttribute("aria-expanded", String(this.isReviewing));
        this.stepperButton.title = this.isReviewing ? "Close this step" : "Open this step again";
      } else {
        this.stepperButton.removeAttribute("aria-expanded");
        this.stepperButton.removeAttribute("title");
      }
    }
  }
}
